import * as _ from 'lodash';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { UserModel } from 'models';
import { UserService } from 'services/user.service';
import { AvailableLanguage, platformAvailableLanguages } from 'helpers';
import { Dictionary } from 'types';

import { MainCategoryTranslationAndSorting } from 'app/b2b-order/components/category-list/category-list.component';

@Injectable()
export class LanguageService {
    public readonly currentLanguage: BehaviorSubject<AvailableLanguage>;

    private defaultLanguage = AvailableLanguage.En;

    constructor(
        private translateService: TranslateService,
        private userService: UserService,
        private userModel: UserModel
    ) {
        const languageCodes = _.keys(platformAvailableLanguages);
        _.forEach(languageCodes, (languageCode) =>
            i18nIsoCountries.registerLocale(require(`i18n-iso-countries/langs/${languageCode}.json`))
        );

        this.translateService.setFallbackLang(this.defaultLanguage);
        this.currentLanguage = new BehaviorSubject(this.defaultLanguage);

        const language = this.getPreferredLanguage() || this.translateService.getBrowserLang();
        if (language) {
            this.use(language as AvailableLanguage);
        }
    }

    public getPreferredLanguage() {
        return this.userService.getPreferredLanguage();
    }

    public onLangChange() {
        return this.translateService.onLangChange;
    }

    public setPreferredLanguageFC(language: AvailableLanguage) {
        if (language) {
            this.userService.setPreferredLanguage(language);
            this.userModel.setPreferredLanguage(language);
            if (this.getCurrentLanguage() !== language) {
                this.use(language);
            }
        }
    }

    public use(language: AvailableLanguage) {
        if (!this.isValidLanguage(language)) {
            console.warn(`Trying to use unavailable language ${language}, using english instead`);
            language = AvailableLanguage.En;
        }

        this.translateService
            .use(language)
            .toPromise()
            .then(() => this.currentLanguage.next(language));
    }

    public getCurrentLanguage(): AvailableLanguage {
        return this.currentLanguage.getValue();
    }

    public isValidLanguage(language: string): boolean {
        return _.includes(_.values(AvailableLanguage), language);
    }

    public getFAQLanguage(): AvailableLanguage {
        const language = this.getPreferredLanguage();
        return language === AvailableLanguage.De ? AvailableLanguage.De : AvailableLanguage.En;
    }

    public translate(
        keys: string[],
        substitutionData?: Dictionary<any, 'substitutionName', string>
    ): Promise<TranslationMap>;
    public translate(key: string, substitutionData?: Dictionary<any, 'substitutionName', string>): Promise<string>;
    public translate(
        key: string | string[],
        substitutionData?: Dictionary<any, 'substitutionName', string>
    ): Promise<string | TranslationMap> {
        return this.translateService.get(key, substitutionData).toPromise();
    }

    public translateObservable(
        keys: string[],
        substitutionData?: Dictionary<any, 'substitutionName', string>
    ): Observable<TranslationMap>;
    public translateObservable(
        key: string,
        substitutionData?: Dictionary<any, 'substitutionName', string>
    ): Observable<string>;
    public translateObservable(
        key: string | string[],
        substitutionData?: Dictionary<any, 'substitutionName', string>
    ): Observable<string | TranslationMap> {
        return this.translateService.get(key, substitutionData);
    }

    public translateTextForCategory(category: string, translations: MainCategoryTranslationAndSorting): string {
        const getTranslationWithFallback = (translation, languages) =>
            translation?.value || _.get(_.find(languages, { language: 'en' }), 'value') || category;

        const findTranslation = (languages) =>
            _.find(languages, (language) => language.language === this.getCurrentLanguage());

        for (const main of translations.mainCategory) {
            if (main.value === category) {
                const translation = findTranslation(main.languages);
                return getTranslationWithFallback(translation, main.languages);
            }

            for (const level1 of main.level1) {
                if (level1.value === category) {
                    const translation = findTranslation(level1.languages);
                    return getTranslationWithFallback(translation, level1.languages);
                }
            }
        }

        return category;
    }

    public getBrowserLanguage() {
        return this.translateService.getBrowserLang();
    }
}

export interface TranslationMap {
    [key: string]: string;
}
